import lodash from 'lodash';

import type { Node, RichContent } from './../ricos-schema/legacy/wix/rich_content/v1/index.js';
import bulletedListDataDefaults from './../ricos-schema/legacy/statics/bulleted_list.defaults.json.js';
import orderedListDataDefaults from './../ricos-schema/legacy/statics/ordered_list.defaults.json.js';
import paragraphDataDefaults from './../ricos-schema/legacy/statics/paragraph.defaults.json.js';
import tableDataDefaults from './../ricos-schema/legacy/statics/table.defaults.json.js';

import { modify } from './RicosContentAPI/modify.js';

// This is normalization of the future RicosDocument
// For now its just a helper for tests
export const normalizeRicosDocument = (content: RichContent): RichContent => {
  const result = modify(content).set(node => {
    return lodash.flow(
      removeEmptyDecorations,
      removeEmptyNodesField,
      removeDefaultsFromDataFields,
      removeUndefinedFields
    )(node);
  });
  return cleanDocumentStyle(removeMetadata(result));
};

const removeUndefinedFields = (node: Node): Node => {
  const result = Object.entries(node).reduce((acc, [key, value]) => {
    if (value === undefined) {
      return acc;
    }
    // @ts-ignore
    acc[key] = value;
    return acc;
  }, {} as Node);
  return result;
};

const removeMetadata = (content: RichContent): RichContent => {
  const { metadata: _, ...rest } = content;
  return rest;
};

const cleanDocumentStyle = (content: RichContent): RichContent => {
  const { documentStyle, ...rest } = content;
  if (!documentStyle || !Object.keys(documentStyle).length) {
    return rest;
  }
  const result = { ...rest, documentStyle: removeDefaults(documentStyle, documentStyleDefaults) };
  return result;
};

const removeEmptyDecorations = (node: Node) => {
  if (node.type === 'TEXT') {
    if (node.textData?.decorations?.length) {
      return node;
    }
    return { ...node, textData: lodash.omit(node.textData, 'decorations') };
  }
  return node;
};

const removeEmptyNodesField = (node: Node) => {
  return !node.nodes?.length ? lodash.omit(node, 'nodes') : node;
};

const removeDefaultsFromDataFields = (node: Node) => {
  if (node.type === 'PARAGRAPH') {
    const { paragraphData, ...rest } = node;
    const normalizedData = removeDefaults(paragraphData ?? {}, paragraphDataDefaults);
    return { ...rest, ...(normalizedData ? { paragraphData: normalizedData } : {}) };
  }

  if (node.type === 'BULLETED_LIST') {
    const { bulletedListData, ...rest } = node;
    const normalizedData = removeDefaults(bulletedListData ?? {}, bulletedListDataDefaults);
    return {
      ...rest,
      // For now we cannot remove the indentation since it is int32 in proto (not optional)
      ...(normalizedData ? { bulletedListData: { indentation: 0, ...normalizedData } } : {}),
    };
  }

  if (node.type === 'TABLE') {
    const { tableData, ...rest } = node;
    const normalizedData = removeDefaults(tableData ?? {}, tableDataDefaults);
    return {
      ...rest,
      ...(normalizedData ? { tableData: normalizedData } : {}),
    };
  }

  if (node.type === 'ORDERED_LIST') {
    const { orderedListData, ...rest } = node;
    const normalizedData = removeDefaults(orderedListData ?? {}, orderedListDataDefaults);
    return {
      ...rest,
      // For now we cannot remove the indentation since it is int32 in proto (not optional)
      ...(normalizedData ? { orderedListData: { indentation: 0, ...normalizedData } } : {}),
    };
  }
  return node;
};

function removeDefaults(
  data: Record<string, any>,
  defaults: Record<string, any>
): Record<string, any> | undefined {
  const result = Object.entries(data).reduce<Record<string, any>>((acc, [key, value]) => {
    if (value === undefined) {
      return acc;
    }
    if (lodash.isEqual(value, defaults[key])) {
      return acc;
    }
    if (lodash.isPlainObject(value) && lodash.isPlainObject(defaults[key])) {
      const nested = removeDefaults(value, defaults[key]);
      if (nested !== undefined) {
        acc[key] = nested;
      }
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});

  return Object.keys(result).length ? result : undefined;
}

const textNodeStyleDefaults = {
  decorations: [],
  nodeStyle: {},
};

const documentStyleDefaults: NonNullable<RichContent['documentStyle']> = {
  headerOne: textNodeStyleDefaults,
  headerTwo: textNodeStyleDefaults,
  headerThree: textNodeStyleDefaults,
  headerFour: textNodeStyleDefaults,
  headerFive: textNodeStyleDefaults,
  headerSix: textNodeStyleDefaults,
  paragraph: textNodeStyleDefaults,
  blockquote: textNodeStyleDefaults,
  codeBlock: textNodeStyleDefaults,
};
